import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { formatDateTimeIT, formatTimelineIT, formatDateIT } from "@/utils/datetime";

import {
  Search,
  Loader2,
  MapPin,
  Calendar,
  LogIn,
  AlertCircle,
  Clock,
  Download,
  Eye,
  EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import CustomerDashboard from "@/components/customer/CustomerDashboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusMap = {
  in_attesa: { label: "Presa in Carico", color: "bg-zinc-400", text: "text-zinc-600" },
  ritirato: { label: "Ritirato", color: "bg-blue-500", text: "text-blue-600" },
  in_transito: { label: "In Transito", color: "bg-[#FF5200]", text: "text-[#FF5200]" },
  in_consegna: { label: "In Consegna", color: "bg-amber-500", text: "text-amber-600" },
  consegnato: { label: "Consegnato", color: "bg-green-500", text: "text-green-600" },
};

export default function TrackingPage() {
  const [searchParams] = useSearchParams();
  const [trackingNumber, setTrackingNumber] = useState("");
  const [trackingResult, setTrackingResult] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingError, setTrackingError] = useState("");

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [token, setToken] = useState(localStorage.getItem("mv_token") || "");
  const [user, setUser] = useState(null);

  const [showTrackingBox, setShowTrackingBox] = useState(false);

  const handleSearch = useCallback(async (number) => {
    const num = number || trackingNumber;
    if (!num.trim()) return;
    setTrackingLoading(true);
    setTrackingError("");
    setTrackingResult(null);
    try {
      const res = await axios.get(`${API}/tracking/${num.trim()}`);
      setTrackingResult(res.data);
    } catch (err) {
      setTrackingError(
        err.response?.data?.detail || "Spedizione non trovata. Verifica il numero di tracking."
      );
    } finally {
      setTrackingLoading(false);
    }
  }, [trackingNumber]);

  useEffect(() => {
    const q = searchParams.get("q") || searchParams.get("n");
    if (q) {
      setTrackingNumber(q);
      setShowTrackingBox(true);
      handleSearch(q);
    }
  }, [searchParams, handleSearch]);

  useEffect(() => {
    if (token) {
      axios
        .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setUser(r.data))
        .catch(() => {
          localStorage.removeItem("mv_token");
          setToken("");
        });
    }
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      toast.error("Inserisci email e password");
      return;
    }
    setLoginLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, {
        email: loginEmail,
        password: loginPassword,
      });
      localStorage.setItem("mv_token", res.data.token);
      setToken(res.data.token);
      setUser(res.data);
      toast.success(`Benvenuto, ${res.data.name}!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Credenziali non valide");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("mv_token");
    setToken("");
    setUser(null);
    toast.info("Disconnesso");
  };

  const handleTrackingSubmit = (e) => {
    e.preventDefault();
    handleSearch();
  };

  return (
    <main>
      {/* Hero */}
      <section className="bg-[#09090B] pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-[#FF5200] text-sm font-bold tracking-widest uppercase mb-4 font-heading">
              Area Clienti
            </p>
            <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight uppercase leading-[0.9]">
              La tua area
              <br />
              <span className="text-[#FF5200]">riservata</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Wrapper to reorder sections: Client Area (login) FIRST, Tracking (secondary) AFTER */}
      <div className="flex flex-col">
      {/* Tracking Search - secondary, only shown when toggled */}
      {!token && showTrackingBox && (
      <section className="order-2 py-10 md:py-12 bg-white border-b-2 border-zinc-200" data-testid="tracking-search-section">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <h2 className="font-heading text-xl md:text-2xl font-bold uppercase tracking-tight text-[#09090B] mb-5 text-center">
            Traccia una spedizione
          </h2>
          <form onSubmit={handleTrackingSubmit} className="flex gap-0" data-testid="tracking-form">
            <input
              type="text"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              placeholder="es. MV-240001"
              className="flex-1 h-14 px-5 border-2 border-zinc-200 border-r-0 rounded-sm focus:border-[#FF5200] outline-none font-body text-lg"
              data-testid="tracking-input"
            />
            <button
              type="submit"
              disabled={trackingLoading}
              className="h-14 px-8 bg-[#FF5200] text-white font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors duration-200 flex items-center gap-2"
              data-testid="tracking-submit-btn"
            >
              {trackingLoading ? <Loader2 size={20} className="animate-spin" /> : <Search size={20} />}
            </button>
          </form>
        </div>
      </section>
      )}

      {/* Tracking Result */}
      {(trackingResult || trackingError) && (
        <section className="order-3 py-12 md:py-16 bg-[#FAFAFA] border-b-2 border-zinc-200" data-testid="tracking-result-section">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            {trackingError && (
              <div className="flex items-center gap-3 p-6 border-2 border-red-200 bg-red-50" data-testid="tracking-error">
                <AlertCircle size={24} className="text-red-500 flex-shrink-0" />
                <p className="text-red-700 font-body">{trackingError}</p>
              </div>
            )}
            {trackingResult && (
              <div data-testid="tracking-result">
                <div className="border-2 border-zinc-200 bg-white p-6 md:p-8 mb-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                    <div>
                      <p className="text-sm font-bold tracking-widest uppercase text-zinc-500 font-heading mb-1">Tracking Number</p>
                      <p className="text-2xl font-bold font-heading text-[#09090B]">{trackingResult.tracking_number}</p>
                    </div>
                    <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-sm text-white font-bold text-sm uppercase tracking-wide font-heading ${statusMap[trackingResult.status]?.color || "bg-zinc-400"}`}>
                      {statusMap[trackingResult.status]?.label || trackingResult.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="flex items-start gap-3">
                      <MapPin size={20} className="text-[#FF5200] mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-bold tracking-widest uppercase text-zinc-500 font-heading">Origine</p>
                        <p className="text-[#09090B] font-body">{trackingResult.origin}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <MapPin size={20} className="text-[#FF5200] mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-bold tracking-widest uppercase text-zinc-500 font-heading">Destinazione</p>
                        <p className="text-[#09090B] font-body">{trackingResult.destination}</p>
                      </div>
                    </div>
                    {trackingResult.estimated_delivery && (
                      <div className="flex items-start gap-3">
                        <Calendar size={20} className="text-[#FF5200] mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-bold tracking-widest uppercase text-zinc-500 font-heading">Consegna stimata</p>
                          <p className="text-[#09090B] font-body">
                            {formatDateIT(trackingResult.estimated_delivery, { day: "numeric", month: "long", year: "numeric" })}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* POD Block - shown when delivered */}
                {trackingResult.status === "consegnato" && trackingResult.pod && (
                  <div className="border-2 border-green-200 bg-green-50 p-6 md:p-8 mb-6" data-testid="public-pod-block">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-bold tracking-widest uppercase text-green-700 font-heading mb-1">Prova di consegna disponibile</p>
                        <p className="text-zinc-700 font-body text-sm">
                          Consegnata il {formatDateTimeIT(trackingResult.pod.timestamp)}
                          {trackingResult.pod.signed_by ? ` • Firmato da ${trackingResult.pod.signed_by}` : ""}
                        </p>
                      </div>
                      <a
                        href={`${API}/tracking/${trackingResult.tracking_number}/pod`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 bg-green-600 text-white px-5 py-3 rounded-sm font-bold uppercase tracking-wide hover:bg-green-700 transition-colors text-sm"
                        data-testid="public-pod-download-btn"
                      >
                        <Download size={18} /> Scarica POD
                      </a>
                    </div>
                  </div>
                )}
                {/* Timeline */}
                {trackingResult.status_history && (
                  <div className="border-2 border-zinc-200 bg-white p-6 md:p-8">
                    <h3 className="font-heading text-xl font-bold uppercase mb-6 text-[#09090B]">Cronologia spedizione</h3>
                    <div className="space-y-0">
                      {[...trackingResult.status_history].reverse().map((event, i) => {
                        const st = statusMap[event.status] || { label: event.status, color: "bg-zinc-400", text: "text-zinc-600" };
                        return (
                          <div key={i} className="flex gap-4" data-testid={`timeline-event-${i}`}>
                            <div className="flex flex-col items-center">
                              <div className={`w-4 h-4 rounded-full ${st.color} flex-shrink-0 ${i === 0 ? "ring-4 ring-opacity-20 ring-current" : ""}`} />
                              {i < trackingResult.status_history.length - 1 && <div className="w-0.5 h-full min-h-[60px] bg-zinc-200" />}
                            </div>
                            <div className="pb-8">
                              <p className={`font-bold font-heading text-sm uppercase tracking-wide ${st.text}`}>{st.label}</p>
                              <p className="text-zinc-600 font-body mt-1">{event.note}</p>
                              <div className="flex items-center gap-4 mt-1 text-sm text-zinc-400">
                                {event.location && <span className="flex items-center gap-1"><MapPin size={12} /> {event.location}</span>}
                                <span className="flex items-center gap-1">
                                  <Clock size={12} />
                                  {formatTimelineIT(event.timestamp)}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Client Area */}
      <section className="order-1 py-12 md:py-16 bg-white" data-testid="client-area-section">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="pt-2">
            {!token ? (
              <div>
                <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-[#09090B] mb-8">
                  Accedi all&apos;Area Clienti
                </h2>
                <div className="max-w-xl">
                  <div>
                    <p className="text-zinc-600 mb-6 font-body">
                      Accedi alla tua area personale per gestire le tue spedizioni,
                      creare nuovi ordini e consultare la tua rubrica indirizzi.
                    </p>
                    <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                      <div>
                        <label htmlFor="tracking-login-email" className="block text-sm font-bold tracking-widest uppercase text-zinc-500 mb-2 font-heading">Email</label>
                        <input
                          id="tracking-login-email"
                          type="email"
                          value={loginEmail}
                          onChange={(e) => setLoginEmail(e.target.value)}
                          className="w-full h-12 px-4 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none transition-colors duration-200 font-body"
                          placeholder="la.tua@email.com"
                          data-testid="login-email-input"
                        />
                      </div>
                      <div>
                        <label htmlFor="tracking-login-password" className="block text-sm font-bold tracking-widest uppercase text-zinc-500 mb-2 font-heading">Password</label>
                        <div className="relative">
                          <input
                            id="tracking-login-password"
                            type={showLoginPassword ? "text" : "password"}
                            value={loginPassword}
                            onChange={(e) => setLoginPassword(e.target.value)}
                            className="w-full h-12 pl-4 pr-12 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none transition-colors duration-200 font-body"
                            placeholder="La tua password"
                            data-testid="login-password-input"
                            autoComplete="off"
                          />
                          <button
                            type="button"
                            onClick={() => setShowLoginPassword(!showLoginPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                            aria-label={showLoginPassword ? "Nascondi password" : "Mostra password"}
                            data-testid="tracking-login-toggle-password"
                            tabIndex={-1}
                          >
                            {showLoginPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>
                      <button
                        type="submit"
                        disabled={loginLoading}
                        className="inline-flex items-center gap-2 bg-[#FF5200] text-white px-8 py-3 font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors duration-200 font-heading text-sm disabled:opacity-50"
                        data-testid="login-submit-btn"
                      >
                        {loginLoading ? <Loader2 size={18} className="animate-spin" /> : <LogIn size={18} />}
                        Accedi
                      </button>
                    </form>
                    {/* Secondary tracking option */}
                    <div className="mt-8 pt-6 border-t border-zinc-200">
                      <p className="text-xs text-zinc-500 mb-2 font-body">
                        Non sei un cliente registrato e vuoi solo seguire una spedizione?
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setShowTrackingBox((v) => !v);
                          setTimeout(() => document.querySelector('[data-testid="tracking-search-section"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
                        }}
                        className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-zinc-700 hover:text-[#FF5200] underline underline-offset-4"
                        data-testid="toggle-tracking-link"
                      >
                        <Search size={14} />
                        {showTrackingBox ? "Nascondi tracking pubblico" : "Traccia una spedizione con il numero"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <CustomerDashboard user={user} token={token} onLogout={handleLogout} />
            )}
          </div>
        </div>
      </section>
      </div>
    </main>
  );
}
